import { useEffect, type CSSProperties, type DetailedHTMLProps, type HTMLAttributes } from 'react'
import { cn } from '../lib/cn.js'
import './eco/eco.css'

/**
 * The EcoPest loader, from Peter's loader kit, in React.
 *
 * The element is the kit's own `<ecopest-loader>`, defined by
 * `public/ecopest-loader.js`, which `index.html` loads before the bundle so the
 * first-load overlay is up while the app is still on its way. Everything here
 * only decides which of its three sizes to draw:
 *
 * - **page** is the boot overlay. `index.html` puts one up; `BootHold` keeps
 *   it while the session is read and `main.tsx` lets it go once the app has
 *   drawn anything else.
 * - **section** sits over a panel or a list while it loads (`LoadingPanel`,
 *   and over `SkeletonRows`). It fades in after a moment, so a quick answer
 *   never flashes the logo.
 * - **inline** is `Spinner`: the green "e" turning, in the colour of the text
 *   around it, so it reads on a Charcoal button as well as on paper.
 *
 * Under jsdom the element is never defined and draws nothing; the tests see
 * an inert tag with its attributes. Decision 116.
 */

type LoaderAttributes = DetailedHTMLProps<HTMLAttributes<HTMLElement>, HTMLElement> & {
  size?: 'page' | 'section' | 'inline'
  mode?: 'reveal' | 'cycle' | 'wipe' | 'spin'
  crop?: 'full' | 'wordmark' | 'symbol'
  label?: string
  progress?: number
  mono?: ''
  keep?: ''
  'no-meter'?: ''
  'no-rings'?: ''
  'min-duration'?: number
}

declare module 'react' {
  namespace JSX {
    interface IntrinsicElements {
      'ecopest-loader': LoaderAttributes
    }
  }
}

interface LoaderElement extends HTMLElement {
  done(): Promise<void>
}

interface LoaderKit {
  page(options?: { label?: string }): LoaderElement
}

function kit(): LoaderKit | null {
  return (window as unknown as { EcopestLoader?: LoaderKit }).EcopestLoader ?? null
}

/** Tailwind's `size-N` as pixels, so the mark is drawn the size the old spinner was. */
function pixelsOf(className: string | undefined, fallback: number): number {
  const match = className?.match(/(?:^|\s)size-(\d+(?:\.\d+)?)(?:\s|$)/)
  return match ? Number(match[1]) * 4 : fallback
}

/**
 * The inline mark. `current` turns the "e" in the colour of the text around
 * it, which is what the old ring did and what keeps it visible on a green or a
 * Charcoal button; `brand` wipes the green through a faded mark, for a line of
 * text on paper ("Loading properties…").
 */
export function Spinner({ className, tone = 'current' }: { className?: string; tone?: 'current' | 'brand' }) {
  const px = pixelsOf(className, 16)
  return (
    <ecopest-loader
      size="inline"
      crop="symbol"
      mode={tone === 'brand' ? 'wipe' : 'spin'}
      mono={tone === 'current' ? '' : undefined}
      role="presentation"
      aria-hidden="true"
      className={cn('inline-grid size-4 shrink-0 place-items-center', className)}
      style={{ '--ecopest-size': `${px}px` } as CSSProperties}
    />
  )
}

/**
 * A panel, list or window that is waiting for its first answer. The logo
 * traces and fills over the space the content will take, and only after a
 * moment, so a fast answer goes straight to the content.
 */
export function LoadingPanel({
  label,
  className,
  instant = false,
}: {
  label?: string
  className?: string
  /** Show at once rather than after the short wait. */
  instant?: boolean
}) {
  return (
    <div
      className={cn('relative grid min-h-40 w-full place-items-center', className)}
      role="status"
      aria-label={label ?? 'Loading'}
    >
      <ecopest-loader
        size="section"
        mode="cycle"
        label={label}
        aria-hidden="true"
        className={cn('ecopest-section', instant ? null : 'ecopest-delay')}
      />
    </div>
  )
}

/** The same mark laid over rows that are already drawn as a skeleton. */
export function SectionOverlay({ label }: { label?: string }) {
  return (
    <ecopest-loader
      size="section"
      mode="cycle"
      no-meter=""
      label={label}
      aria-hidden="true"
      className="ecopest-section ecopest-delay ecopest-veil"
    />
  )
}

/* -------------------------------------------------------------------------- */
/* The boot overlay                                                           */
/* -------------------------------------------------------------------------- */

const BOOT_ID = 'boot'
const HOLD_ATTRIBUTE = 'data-boot-hold'

/**
 * Keeps the first-load overlay up while it is rendered — the session gate, as
 * the app works out whether anyone is signed in. When there is no overlay to
 * keep (a test, a hot reload), it puts one up after the same short wait the
 * kit's `during` uses, and lets it go when it unmounts.
 */
export function BootHold() {
  useEffect(() => {
    if (document.getElementById(BOOT_ID)) return
    let own: LoaderElement | null = null
    const timer = window.setTimeout(() => {
      own = kit()?.page({ label: 'Loading' }) ?? null
    }, 150)
    return () => {
      window.clearTimeout(timer)
      void own?.done()
    }
  }, [])
  return <span hidden {...{ [HOLD_ATTRIBUTE]: '' }} />
}

/**
 * Lets the boot overlay go the first time the app has drawn something that is
 * not a `BootHold`: the board, the login page, or a customer's proposal link,
 * which never reads the session at all. A minute is the backstop, so nothing
 * can leave a person staring at the logo.
 */
export function releaseBootWhenDrawn(root: HTMLElement): void {
  const boot = document.getElementById(BOOT_ID) as LoaderElement | null
  if (!boot) return
  let released = false
  const release = () => {
    if (released) return
    released = true
    observer.disconnect()
    window.clearTimeout(backstop)
    if (typeof boot.done === 'function') void boot.done()
    else boot.remove()
  }
  const check = () => {
    if (root.childElementCount > 0 && !root.querySelector(`[${HOLD_ATTRIBUTE}]`)) release()
  }
  const observer = new MutationObserver(check)
  observer.observe(root, { childList: true, subtree: true })
  const backstop = window.setTimeout(release, 60_000)
  check()
}
